import type { GridAggregationModel } from '@mui/x-data-grid-premium';
import { RiArrowDownSLine, RiDeleteBinLine, RiEditLine } from '@remixicon/react';
import { useEffect, useState } from 'react';
import {
  AgButton,
  AgInput,
  AgModal,
  AgModalActions,
  AgModalContent,
  AgPopoverTrigger,
  AgText,
} from 'src/components/ui';
import ConfirmModal from '../../../components/containers/modals/ConfirmModal';
import type { BaseGridStateWithHelpers } from '../shared/baseGridTypes';
import { useGridViewConfig } from '../views/useGridViewConfig';

type ViewConfigurationMenuProps = {
  page: string;
  enhancedGridState?: BaseGridStateWithHelpers;
  /**
   * When provided, these aggregation entries are forced over the saved config's aggregation
   * model on every view restore (the saved view's other aggregations are preserved). Prevents
   * stale/unsafe aggregations (e.g. TAS-3328's repackDate:'max') saved before a fix shipped from
   * resurrecting once the view is reloaded.
   */
  aggregationModel?: GridAggregationModel;
  'data-testid'?: string;
};

const menuItemClass =
  'flex items-center gap-2 px-3 py-1.5 text-14 text-neutral-text cursor-pointer rounded-md hover:bg-neutral-3 transition-colors';

export default function ViewConfigurationMenu({
  page,
  enhancedGridState,
  aggregationModel,
  'data-testid': dataTestId,
}: ViewConfigurationMenuProps) {
  const {
    views,
    selectedView,
    canSaveChanges,
    selectView,
    selectDefaultView,
    saveChangesToSelectedView,
    createDialogOpen,
    openCreateDialog,
    closeCreateDialog,
    confirmCreate,
    viewToRename,
    startRename,
    cancelRename,
    confirmRename,
    viewToDelete,
    startDelete,
    cancelDelete,
    confirmDelete,
  } = useGridViewConfig({ page, enhancedGridState, aggregationModel });

  return (
    <>
      <AgPopoverTrigger
        trigger={
          <>
            {`Views${selectedView?.name ? ` (${selectedView.name})` : ''}`}
            <RiArrowDownSLine size={16} />
          </>
        }
        variant="white"
        size="sm"
        className="gap-1"
        data-testid={dataTestId}
      >
        {(close) => (
          <div className="min-w-64 p-1.5">
            {views.length > 0 && (
              <div className="px-2 py-1.5">
                <AgText size="12" className="uppercase tracking-wider">
                  YOUR VIEWS
                </AgText>
              </div>
            )}
            {views.map((viewConfig) => {
              const selected = selectedView?.id === viewConfig.id;
              return (
                <div
                  key={viewConfig.id}
                  role="menuitem"
                  tabIndex={0}
                  className={`${menuItemClass} w-full text-left ${selected ? 'bg-brand-alpha-1' : ''}`}
                  onClick={() => {
                    selectView(viewConfig);
                    close();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      selectView(viewConfig);
                      close();
                    }
                  }}
                >
                  <span className="flex-1 truncate">{viewConfig.name}</span>
                  <AgButton
                    variant="ghost"
                    size="icon-xs"
                    shape="circle"
                    onPress={() => {
                      close();
                      startRename(viewConfig);
                    }}
                  >
                    <RiEditLine size={16} />
                  </AgButton>
                  <AgButton
                    variant="ghost"
                    size="icon-xs"
                    shape="circle"
                    onPress={() => {
                      close();
                      startDelete(viewConfig);
                    }}
                  >
                    <RiDeleteBinLine size={16} />
                  </AgButton>
                </div>
              );
            })}
            {views.length > 0 && <div className="my-1 h-px bg-neutral-5" />}
            <button
              className={`${menuItemClass} w-full text-left`}
              onClick={() => {
                selectDefaultView();
                close();
              }}
            >
              Default view
            </button>
            <div className="my-1 h-px bg-neutral-5" />
            <button
              className={`${menuItemClass} w-full text-left ${!canSaveChanges ? 'opacity-40 pointer-events-none' : ''}`}
              disabled={!canSaveChanges}
              onClick={() => {
                saveChangesToSelectedView();
                close();
              }}
            >
              Save changes
            </button>
            <button
              className={`${menuItemClass} w-full text-left`}
              onClick={() => {
                openCreateDialog();
                close();
              }}
            >
              Save as new view
            </button>
          </div>
        )}
      </AgPopoverTrigger>
      <ViewNameDialog
        open={createDialogOpen}
        handleClose={closeCreateDialog}
        handleConfirm={confirmCreate}
        confirmText="Create view"
      />
      <ViewNameDialog
        open={!!viewToRename}
        initialValue={viewToRename?.name}
        handleClose={cancelRename}
        handleConfirm={confirmRename}
        confirmText="Rename view"
      />
      <ConfirmModal
        title="Delete view"
        prompt={`Are you sure you want to delete the view "${viewToDelete?.name}"?`}
        open={viewToDelete !== null}
        handleClose={cancelDelete}
        handleConfirm={confirmDelete}
      />
    </>
  );
}

const ViewNameDialog = ({
  open,
  handleClose,
  confirmText,
  handleConfirm,
  initialValue = '',
}: {
  open: boolean;
  handleClose: () => void;
  confirmText: string;
  handleConfirm: (name: string) => void;
  initialValue?: string;
}) => {
  const [name, setName] = useState(initialValue);

  // Reset name when dialog opens with a new initialValue
  useEffect(() => {
    if (open) {
      setName(initialValue);
    }
  }, [open, initialValue]);

  const close = () => {
    setName('');
    handleClose();
  };

  const confirm = () => {
    handleConfirm(name);
    setName('');
  };

  return (
    <AgModal open={open} onClose={close} title={confirmText} size="sm">
      <AgModalContent>
        <AgInput autoFocus value={name} onChange={setName} placeholder="Name" />
      </AgModalContent>
      <AgModalActions>
        <AgButton variant="primary" isDisabled={name.length === 0} onPress={() => confirm()}>
          {confirmText}
        </AgButton>
      </AgModalActions>
    </AgModal>
  );
};
